<script>
import { preventDefault } from "svelte/legacy";
import { onMount } from "svelte";

import { modalState, hideModal } from "@app/store/modalStore.svelte.js";
import { insertHistoryAction } from "@app/store/historyStore.svelte.js";
import { projectState } from "@app/store/projectStore.svelte.js";
import { toast } from "@zerodevx/svelte-toast";
import { fade } from "svelte/transition";
import { eventGroupState } from "@app/store/eventGroupStore.svelte.js";
import { authState } from "@app/store/authStore.svelte.js";
import { tick } from "svelte";
import eol from "eol";
import * as m from "@app/paraglide/messages.js";

/* CC LIB */
import * as mammoth from 'mammoth/mammoth.browser';
import _Event from "@cclib/classes/event.js";
import _EventGroup from "@cclib/classes/eventGroup.js";
import defaults from "@cclib/lib/defaults.js";
import encodings from "@cclib/dict/encodings.js";
import convertToHtml from "@cclib/functions/quill/convertToHtml.js";
import convertToPlainText from "@cclib/functions/quill/convertToPlainText.js";
import LanguageSelect from "@app/components/inputs/LanguageSelect.svelte";

let eventGroupDefaults =
    JSON.parse(localStorage.getItem("cc-event-group-defaults")) || {};
let plaintextImportDefaults =
    JSON.parse(localStorage.getItem("cc-plaintext-import-defaults")) || {};

/* Default Settings */
let srcFileName,
    showOptions = $state(false),
    files = $state(),
    fileEncoding = $state(defaults.encoding),
    fileContents = $state(""),
    eventGroup = $state("new"),
    groupType = $state("subtitle"),
    importOption = $state("merge"),
    language = $state(eventGroupDefaults.language || "en-US"),
    rtl = $state(eventGroupDefaults.rtl),
    /* Import Options */
    blockImport = $state(false), // Default to false, removing from UI
    eventLines = $state(plaintextImportDefaults.eventLines || 1),
    endEventOnPunctuation = $state(
        plaintextImportDefaults.endEventOnPunctuation,
    ),
    endEventOnComma = $state(plaintextImportDefaults.endEventOnComma),
    endEventOnCharOption = $state(plaintextImportDefaults.endEventOnCharOption),
    endEventOnChar = $state(plaintextImportDefaults.endEventOnChar),
    startEventOnCharOption = $state(
        plaintextImportDefaults.startEventOnCharOption,
    ),
    startEventOnChar = $state(plaintextImportDefaults.startEventOnChar),
    maxChars = $state(plaintextImportDefaults.maxChars || 32),
    ignoreTimecodes = $state(plaintextImportDefaults.ignoreTimecodes || false),
    targetPreview = $state("");

// Common abbreviations to ignore when checking for end-of-sentence punctuation
const commonAbbreviations = [
    "mr", "mrs", "ms", "dr", "prof", "rev", "hon", "st", "rd", "ave", "blvd", 
    "apt", "jr", "sr", "vol", "etc", "vs", "fig", "ca", "esq", "inc", "co", 
    "ltd", "corp", "no", "al", "cf", "ed", "eds", "est", "e.g", "i.e", "p.m", 
    "a.m", "u.s", "u.k", "ft", "gov", "sen", "rep", "capt"
];

// Function to check if word is a common abbreviation
function isCommonAbbreviation(word) {
    if (!word) return false;
    
    // Remove the trailing punctuation for comparison
    const wordWithoutPunctuation = word.replace(/[.!?]$/, "").toLowerCase();
    return commonAbbreviations.includes(wordWithoutPunctuation);
}

// Function to strip timecodes from text
function stripTimecodes(text) {
    if (!ignoreTimecodes) return text;
    
    // Remove timecodes in formats: 
    // - hh:mm:ss:ff
    // - hh:mm:ss
    // - hh:mm:ss:mmm
    // - hh:mm:ss,mmm (SRT format)
    return text.replace(/\b\d{1,2}:\d{2}:\d{2}([:,.]\d{2,3})?\b/g, "")
              .replace(/^\s*[\r\n]/gm, "\n") // Remove empty lines
              .replace(/\n{3,}/g, "\n\n");    // Remove excessive line breaks
}

// Handler for file inputs
function handleFileInput(event) {
    files = Array.from(event.currentTarget.files || []);
    updateFile();
}

function updateFile() {
    if (files && files[0]) {
        let fileName = files[0].name.split(".");
        let fileExt = fileName.pop();
        srcFileName = fileName.join(".");
        updateFileContents(fileExt);
    }
}

async function updateFileContents(fileExt = "") {
    if (!files || !files[0]) return;
    
    toast.push(m.plain_text_import_reading_file_toast(), {
        classes: ["toast-warning"],
    });
    
    try {
        // Handle DocX files
        if (fileExt.toLowerCase() === "docx") {
            let reader = new FileReader();
            reader.onload = async function (e) {
                try {
                    // Use mammoth to extract text from DocX
                    let rawText = await mammoth.extractRawText({ arrayBuffer: e.target.result });
                    fileContents = stripTimecodes(rawText.value);
                    updateTargetPreview();
                } catch (error) {
                    toast.push(
                        m.plain_text_import_docx_error_toast({
                            error: error.message,
                        }),
                        { classes: ["toast-error"] },
                    );
                }
            };
            reader.onerror = () => {
                toast.push(m.plain_text_import_read_error_toast(), {
                    classes: ["toast-error"],
                });
            };
            reader.readAsArrayBuffer(files[0]);
        } else {
            // Handle plain text files
            let reader = new FileReader();
            reader.onload = function (e) {
                fileContents = stripTimecodes(e.target.result);
                updateTargetPreview();
            };
            reader.onerror = () => {
                toast.push(m.plain_text_import_read_error_toast(), {
                    classes: ["toast-error"],
                });
            };
            reader.readAsText(files[0], fileEncoding);
        }
    } catch (error) {
        toast.push(
            m.plain_text_import_read_error_with_message_toast({
                error: error.message,
            }),
            { classes: ["toast-error"] },
        );
    }
}

// Generate preview of how the imported text will be formatted
function updateTargetPreview() {
    if (!fileContents) {
        targetPreview = "";
        return;
    }

    let previewEvents = [];
    let alignment = "center";
    // Always use user-selected values regardless of option flags for preview
    let maximumChars = maxChars;
    let maxLines = eventLines;
    let blockCount = 0; // Count of text blocks for limiting preview
    const MAX_PREVIEW_BLOCKS = 25; // Maximum number of blocks to preview

    // Use the same logic as the import function, but just for preview
    const moreBlocksSuffix = m.plain_text_import_preview_more_blocks_suffix();

    if (blockImport) {
        let paragraphs = eol.lf(fileContents).split("\n\n");
        // Limit to first 25 paragraphs
        if (paragraphs.length > MAX_PREVIEW_BLOCKS) {
            targetPreview = paragraphs
                .slice(0, MAX_PREVIEW_BLOCKS)
                .join("\n\n");
            targetPreview +=
                "\n\n" +
                m.plain_text_import_preview_more_blocks({
                    count: paragraphs.length - MAX_PREVIEW_BLOCKS,
                });
        } else {
            targetPreview = paragraphs.join("\n\n");
        }
    } else {
        let paragraphs = eol.lf(fileContents).split("\n\n");
        let formattedParagraphs = [];
        let totalOriginalParagraphs = paragraphs.length;

        // Process only until we hit the maximum preview blocks or run out of paragraphs
        for (let paragraph of paragraphs) {
            if (blockCount >= MAX_PREVIEW_BLOCKS) break;

            let formattedParagraph = "";
            let paragraphLines = paragraph.split("\n");

            for (let pLine of paragraphLines) {
                if (blockCount >= MAX_PREVIEW_BLOCKS) break;

                let words = pLine.split(" ");
                let line = "";
                let eventLines = [];

                for (let word of words) {
                    if (blockCount >= MAX_PREVIEW_BLOCKS) break;

                    if (line.length + word.length + 1 > maximumChars) {
                        eventLines.push(line.trim());
                        line = word;
                    } else {
                        line += (line ? " " : "") + word;
                    }

                    let endEventFlag = false;

                    if (endEventOnCharOption && word.includes(endEventOnChar)) {
                        endEventFlag = true;
                    }
                    if (endEventOnComma && word.includes(",")) {
                        endEventFlag = true;
                    }
                    if (endEventOnPunctuation && word.match(/\.|\?|\!|\;/g)) {
                        // Skip ending event if the word is a common abbreviation
                        if (!isCommonAbbreviation(word)) {
                            endEventFlag = true;
                        }
                    }

                    // Check if we need to end this event
                    if (
                        endEventFlag &&
                        (line.length > 0 || eventLines.length > 0)
                    ) {
                        if (line.length > 0) {
                            eventLines.push(line.trim());
                            line = "";
                        }

                        if (eventLines.length > 0) {
                            formattedParagraph +=
                                (formattedParagraph ? "\n\n" : "") +
                                eventLines.join("\n");
                            eventLines = [];
                            blockCount++; // Increment block count
                        }

                        endEventFlag = false;
                    }

                    // Check if we've reached the max lines limit for an event
                    if (eventLines.length >= maxLines) {
                        formattedParagraph +=
                            (formattedParagraph ? "\n\n" : "") +
                            eventLines.join("\n");
                        eventLines = [];
                        blockCount++; // Increment block count
                    }
                }

                if (line.length > 0) {
                    eventLines.push(line.trim());
                }

                if (eventLines.length > 0) {
                    formattedParagraph +=
                        (formattedParagraph ? "\n\n" : "") +
                        eventLines.join("\n");
                    blockCount++; // Increment block count
                }
            }

            formattedParagraphs.push(formattedParagraph);

            // Early exit if we've reached the maximum block count
            if (blockCount >= MAX_PREVIEW_BLOCKS) {
                break;
            }
        }

        targetPreview = formattedParagraphs.join("\n\n");

        // Add a message if we limited the preview
        if (
            blockCount >= MAX_PREVIEW_BLOCKS ||
            paragraphs.length > formattedParagraphs.length
        ) {
            // Calculate approximately how many more blocks might be generated
            let remainingParagraphs =
                totalOriginalParagraphs - formattedParagraphs.length;
            let estimatedRemainingBlocks =
                remainingParagraphs * (blockCount / formattedParagraphs.length);

            // targetPreview += `\n\n... (approximately ${Math.round(estimatedRemainingBlocks)} more blocks not shown)`;
        }
    }
}

async function importTranscriptFile() {
    toast.push(m.plain_text_import_importing_events_toast(), {
        classes: ["toast-warning"],
    });

    let events = [],
        event,
        alignment = "center",
        endEventFlag = false;

    // Match preview logic by directly using values without option flags
    let maximumChars = maxChars;
    let maxLines = eventLines;

    if (eventGroupState[projectState.selected]) {
        eventGroupState[projectState.selected].selected = [];
        await tick();
    }

    /* Create or select the event group */
    if (eventGroup === "new") {
        let eventGroupOptions = {
            type: groupType,
            name: srcFileName,
            language: language,
            rtl: rtl,
            maxLines : eventGroupDefaults.maxLines || 2,
            maxChars : eventGroupDefaults.maxChars || 32,
            overlap : eventGroupDefaults.overlap || false,
            minDuration : eventGroupDefaults.minDuration || 0.5,
            maxDuration : eventGroupDefaults.maxDuration || 5,
            fontFamily: eventGroupDefaults.fontFamily ?? "plexMono",
            fontSize: eventGroupDefaults.fontSize || 16,
            events: []
        };

        alignment = "left";
        // Use push method instead of direct assignment
        eventGroupState.push({ ...new _EventGroup(eventGroupOptions) });
        projectState.selected = eventGroupState.length - 1;
    } else {
        projectState.selected = eventGroupState.findIndex((group) => {
            return group.id === eventGroup;
        });

        if (eventGroupState[projectState.selected].type === "subtitle") {
            alignment = "center";
        } else {
            alignment = eventGroupState[projectState.selected].rtl
                ? "right"
                : "left";
        }
    }

    // Consistent parsing logic with preview function
    let paragraphs = eol.lf(fileContents).split("\n\n");

    if (blockImport) {
        // Block import logic (entire paragraph as one event)
        for (let paragraph of paragraphs) {
            event = {
                ...new _Event({
                    text: convertToHtml(paragraph),
                    alignment: alignment,
                }),
            };
            events.push(event);
        }
    } else {
        // Standard import logic, matching the preview logic
        for (let paragraph of paragraphs) {
            let paragraphLines = paragraph.split("\n");

            for (let pLine of paragraphLines) {
                let words = pLine.split(" ");
                let line = "";
                let lines = [];

                event = {
                    ...new _Event({
                        alignment: alignment,
                    }),
                };

                for (let word of words) {
                    // Match preview word boundary check (add +1 for space)
                    if (line.length + word.length + 1 > maximumChars) {
                        lines.push(line.trim());
                        line = word;
                    } else {
                        // Match preview spacing logic
                        line += (line ? " " : "") + word;
                    }

                    endEventFlag = false;

                    if (endEventOnCharOption && word.includes(endEventOnChar)) {
                        endEventFlag = true;
                    }

                    if (endEventOnComma && word.includes(",")) {
                        endEventFlag = true;
                    }

                    if (endEventOnPunctuation && word.match(/\.|\?|\!/g)) {
                        // Skip ending event if the word is a common abbreviation
                        if (!isCommonAbbreviation(word)) {
                            endEventFlag = true;
                        }
                    }

                    if (
                        words[0] &&
                        startEventOnCharOption &&
                        words[0].includes(startEventOnChar)
                    ) {
                        endEventFlag = true;
                    }

                    // Check if we need to end this event (matching preview logic)
                    if (endEventFlag && (line.length > 0 || lines.length > 0)) {
                        if (line.length > 0) {
                            lines.push(line.trim());
                            line = "";
                        }

                        event.text = convertToHtml(lines.join("\n"));
                        events.push(event);

                        lines = [];
                        event = {
                            ...new _Event({
                                alignment: alignment,
                            }),
                        };

                        endEventFlag = false;
                    }

                    // Use '>=' instead of '===' to match preview
                    if (lines.length >= maxLines) {
                        event.text = convertToHtml(lines.join("\n"));
                        events.push(event);

                        lines = [];
                        event = {
                            ...new _Event({
                                alignment: alignment,
                            }),
                        };
                    }
                }

                // Add any remaining content
                if (line.length > 0) {
                    lines.push(line.trim());
                }

                if (lines.length > 0) {
                    event.text = convertToHtml(lines.join("\n"));
                    events.push(event);
                }
            }
        }
    }

    events = events.filter((ccEvent) => {
        return convertToPlainText(ccEvent.text) !== "";
    });

    if (eventGroup === "new" || importOption === "replace") {
        eventGroupState[projectState.selected].events = events;
    } else {
        eventGroupState[projectState.selected].events = [
            ...eventGroupState[projectState.selected].events,
            ...events,
        ];
    }

    eventGroupState[projectState.selected].events = JSON.parse(JSON.stringify(eventGroupState[projectState.selected].events));

    insertHistoryAction({
        name: "import transcript", //action name
        eventGroup: projectState.selected,
        snapshots: [
            {
                store: "eventGroupState",
                value: JSON.stringify(eventGroupState),
            },
        ],
    });

    localStorage.setItem(
        "cc-plaintext-import-defaults",
        JSON.stringify({
            blockImport: false, 
            eventLinesOptions: true,
            eventLines: eventLines,
            endEventOnPunctuation: endEventOnPunctuation,
            endEventOnComma: endEventOnComma,
            endEventOnCharOption: endEventOnCharOption,
            endEventOnChar: endEventOnChar,
            startEventOnCharOption: startEventOnCharOption,
            startEventOnChar: startEventOnChar,
            maxCharsOption: true,
            maxChars: maxChars,
            ignoreTimecodes: ignoreTimecodes
        }),
    );

    hideModal();
}

// Update target preview when settings change
function updatePreviewOnSettingsChange() {
    updateTargetPreview();
}

onMount(() => {
    // Initialize the preview if there's content
    if (fileContents) {
        updateTargetPreview();
    }
});
</script>

<div
    transition:fade={{ duration: 100 }}
    class="modal {modalState.selected === 'plainTextImport'
        ? 'show d-block'
        : ''}"
    role="dialog"
    tabindex="-1"
    id="PlainTextImportModal"
    aria-labelledby="plainTextImportTitle"
    aria-describedby="plainTextImportDescription">
    <div
        class="modal-dialog modal-xl modal-dialog-centered modal-dialog-scrollable"
        role="document">
        <div class="modal-content">
            <div class="modal-header">
                <h4 class="modal-title" id="plainTextImportTitle">
                    {m.plain_text_import_title()}
                </h4>
                <button
                    type="button"
                    class="btn-close"
                    id="closePlainTextImport"
                    aria-label={m.plain_text_import_close_label()}
                    onclick={hideModal}></button>
            </div>
            <div class="modal-body">
                <div id="plainTextImportDescription" class="visually-hidden">
                    {m.plain_text_import_description()}
                </div>
                <p class="small text-muted">
                    <i class="bi bi-lightning-fill"></i>
                    {m.plain_text_import_powered_by()}
                </p>

                <div class="row">
                    <div class="col-md-5">
                        <!-- Import Form Start -->
                        <form onsubmit={preventDefault(importTranscriptFile)}>
                            <!-- File Input -->
                            <div class="mb-3 shadow bg-secondary rounded p-3">
                                <label
                                    class="form-label"
                                    for="subtitleFilePicker"
                                    >{m.plain_text_import_select_file_label()} <i
                                        class="bi bi-info-circle"
                                        title={m.plain_text_import_supported_formats_label({
                                            formats: ".txt, .docx",
                                        })}></i
                                    ></label>
                                <input
                                    type="file"
                                    class="form-control"
                                    id="subtitleFilePicker"
                                    onchange={handleFileInput}
                                    accept=".txt,.docx" />

                                <div class="mb-2 mt-2">
                                    <label
                                        class="form-label"
                                        for="srcFileEncoding"
                                        >{m.plain_text_import_file_encoding_label()}</label>
                                    <select
                                        id="srcFileEncoding"
                                        class="form-select"
                                        bind:value={fileEncoding}
                                        onchange={() => {
                                            updateFileContents();
                                            updateTargetPreview();
                                        }}>
                                        {#each encodings as encodingOption}
                                            <option>{encodingOption}</option>
                                        {/each}
                                    </select>
                                </div>
                            </div>

                            <!-- Key Import Options - Made More Prominent -->
                            <div class="mb-3 shadow bg-secondary rounded p-3">
                                <h5 class="text-warning">
                                    {m.plain_text_import_settings_heading()}
                                </h5>

                                <!-- Max Lines Per Event - Primary Setting -->
                                <div class="mb-3">
                                    <label class="form-label" for="eventLines"
                                        >{m.plain_text_import_max_lines_label()} <i
                                            class="bi bi-info-circle"
                                            title={m.plain_text_import_max_lines_help()}
                                        ></i
                                        ></label>
                                    <input
                                        type="number"
                                        class="form-control"
                                        id="eventLines"
                                        min="1"
                                        step="1"
                                        bind:value={eventLines}
                                        onchange={updatePreviewOnSettingsChange}
                                        oninput={updatePreviewOnSettingsChange} />
                                </div>

                                <!-- Max Characters Per Line - Primary Setting -->
                                <div class="mb-3">
                                    <label class="form-label" for="maxChars"
                                        >{m.plain_text_import_max_chars_label()} <i
                                            class="bi bi-info-circle"
                                            title={m.plain_text_import_max_chars_help()}
                                        ></i></label>
                                    <input
                                        type="number"
                                        class="form-control"
                                        id="maxChars"
                                        min="1"
                                        step="1"
                                        bind:value={maxChars}
                                        onchange={updatePreviewOnSettingsChange}
                                        oninput={updatePreviewOnSettingsChange} />
                                </div>                               
                                
                                <div class="mb-3">
                                    <label
                                        class="form-label"
                                        for="endEventOnCharOption">
                                        {m.plain_text_import_end_event_char_label()}</label>
                                    
                                    <div class="input-group mb-3">
                                        <div class="input-group-text">
                                            <input
                                                class="form-check-input mt-0"
                                                type="checkbox"
                                                bind:checked={
                                                    endEventOnCharOption
                                                }
                                                onchange={updatePreviewOnSettingsChange} />
                                        </div>
                                        <input
                                            type="text"
                                            maxlength="1"
                                            bind:value={endEventOnChar}
                                            disabled={!endEventOnCharOption}
                                            class="form-control"
                                            oninput={updatePreviewOnSettingsChange} />
                                    </div>
                                </div>
                                <div class="mb-3">
                                    <label
                                        class="form-label"
                                        for="startEventOnCharOption"
                                        >{m.plain_text_import_start_event_char_label()}</label>
                                    <div class="input-group mb-3">
                                        <div class="input-group-text">
                                            <input
                                                class="form-check-input mt-0"
                                                type="checkbox"
                                                bind:checked={
                                                    startEventOnCharOption
                                                }
                                                onchange={updatePreviewOnSettingsChange} />
                                        </div>
                                        <input
                                            type="text"
                                            maxlength="1"
                                            bind:value={startEventOnChar}
                                            disabled={!startEventOnCharOption}
                                            class="form-control"
                                            oninput={updatePreviewOnSettingsChange} />
                                    </div>
                                </div>

                                <div class="mb-3 form-check">
                                    <input
                                        id="endEventOnPunctuationOption"
                                        type="checkbox"
                                        class="form-check-input"
                                        bind:checked={endEventOnPunctuation}
                                        onchange={updatePreviewOnSettingsChange} />
                                    <label
                                        class="form-check-label"
                                        for="endEventOnPunctuationOption"
                                        >{m.plain_text_import_end_event_punctuation_label()}</label>
                                </div>
                                <div class="mb-3 form-check">
                                    <input
                                        id="endEventOnCommaOption"
                                        type="checkbox"
                                        class="form-check-input"
                                        bind:checked={endEventOnComma}
                                        onchange={updatePreviewOnSettingsChange} />
                                    <label
                                        class="form-check-label"
                                        for="endEventOnCommaOption"
                                        >{m.plain_text_import_end_event_comma_label()}</label>
                                </div>

                                <!-- Ignore Timecodes Option -->
                                <div class="mb-3 form-check">
                                    <input
                                        id="ignoreTimecodesOption"
                                        type="checkbox"
                                        class="form-check-input"
                                        bind:checked={ignoreTimecodes}
                                        onchange={() => {
                                            if (files && files[0]) {
                                                updateFile();
                                            } else {
                                                updatePreviewOnSettingsChange();
                                            }
                                        }} />
                                    <label
                                        class="form-check-label"
                                        for="ignoreTimecodesOption">
                                        {m.plain_text_import_ignore_timecodes_label()} <i
                                            class="bi bi-info-circle"
                                            title={m.plain_text_import_ignore_timecodes_help()}></i>
                                    </label>
                                </div>
                            </div>

                            <button
                                type="button"
                                class="btn btn-outline-warning mb-2 dropdown-toggle"
                                onclick={() => {
                                    showOptions = !showOptions;
                                }}>{m.plain_text_import_advanced_options_button()}</button>
                            <!-- Start of Advanced Settings -->
                            {#if showOptions}
                                <div
                                    id="advancedOptions"
                                    class="p-3 bg-secondary shadow rounded">
                                    <div class="row">
                                        <!-- Target Event Group Option Start -->
                                        <div class="mb-3 col-md-6">
                                            <label
                                                class="form-label text-warning"
                                                for="EventGroupSelection"
                                                >{m.plain_text_import_event_group_label()}</label>
                                            <select
                                                class="form-select"
                                                bind:value={eventGroup}>
                                                {#each eventGroupState as eventGroupOption}
                                                    <option
                                                        value={eventGroupOption.id}
                                                        >{eventGroupOption.name}</option>
                                                {/each}
                                                <option
                                                    class="fw-bold"
                                                    value="new">{m.new_badge()}</option>
                                            </select>
                                        </div>
                                        <!-- Target Event Group Option End -->

                                        <!-- Merge or Replace Option Start -->
                                        {#if eventGroup !== "new"}
                                            <div class="mb-3 col-md-6">
                                                <p>
                                                    {m.plain_text_import_merge_replace_label()} <i
                                                        class="bi bi-info-circle"
                                                        title={m.plain_text_import_merge_replace_help()}
                                                    ></i>
                                                </p>

                                                <div class="form-check">
                                                    <input
                                                        class="form-check-input"
                                                        type="radio"
                                                        bind:group={
                                                            importOption
                                                        }
                                                        name="importOption"
                                                        id="mergeImportOption"
                                                        value="merge"
                                                        checked />
                                                    <label
                                                        class="form-check-label"
                                                        for="mergeImportOption">
                                                        {m.plain_text_import_merge_label()}
                                                    </label>
                                                </div>
                                                <div class="form-check">
                                                    <input
                                                        class="form-check-input"
                                                        type="radio"
                                                        bind:group={
                                                            importOption
                                                        }
                                                        name="importOption"
                                                        id="replaceImportOption"
                                                        value="replace" />
                                                    <label
                                                        class="form-check-label"
                                                        for="replaceImportOption">
                                                        {m.plain_text_import_replace_label()}
                                                    </label>
                                                </div>
                                            </div>
                                        {:else}
                                            <!-- Language -->
                                            <div class="mb-3 col-md-6">
                                                <label
                                                    class="form-label text-warning"
                                                    for="GroupType"
                                                    >{m.plain_text_import_group_type_label()}</label>
                                                <select
                                                    class="form-select"
                                                    id="GroupType"
                                                    bind:value={groupType}>
                                                    <option value="subtitle">
                                                        {m.subtitle()}
                                                    </option>
                                                    <option
                                                        value="transcription">
                                                        {m.transcription()}
                                                    </option>
                                                    {#if authState.ad}
                                                        <option
                                                            value="audio description">
                                                            {m.audio_description()}
                                                        </option>
                                                    {/if}
                                                </select>
                                            </div>
                                            <!-- Language -->
                                            <div class="mb-3 col-md-6">
                                                <label
                                                    class="form-label text-warning"
                                                    for="Language"
                                                    >{m.plain_text_import_group_language_label()}</label>
                                                <LanguageSelect id="Language" bind:value={language} />
                                            </div>
                                            <div class="mb-3 col-md-6">
                                                <div
                                                    class="form-check form-switch mb-2">
                                                    <input
                                                        type="checkbox"
                                                        class="form-check-input"
                                                        id="rtlSwitch"
                                                        bind:checked={rtl} />
                                                    <label
                                                        class="form-check-label"
                                                        for="rtlSwitch"
                                                        >{m.right_to_left()}</label>
                                                </div>
                                            </div>
                                        {/if}
                                        <!-- Merge or Replace Option End -->
                                    </div>
                                </div>
                                <!-- End of Advanced Settings -->
                            {/if}
                        </form>
                        <!-- Import Form End -->
                    </div>
                    <div class="col-md-7">
                        <!-- Preview Section With Source and Target -->
                        <div class="row">
                            <!-- Source Preview -->
                            <div class="col-md-12">
                                <form id="SourcePreviewForm">
                                    <div class="mb-3">
                                        <label
                                            class="form-label"
                                            for="srcPreviewText"
                                            >{m.plain_text_import_source_preview_label()}</label>
                                        <textarea
                                            bind:value={fileContents}
                                            oninput={updateTargetPreview}
                                            class="form-control"
                                            id="srcPreviewText"
                                            rows="10"
                                            placeholder={m.plain_text_import_source_preview_placeholder()}
                                        ></textarea>
                                    </div>
                                </form>
                            </div>
                            <!-- Target Preview -->
                            <div class="col-md-12">
                                <form id="TargetPreviewForm">
                                    <div class="mb-3">
                                        <label
                                            class="form-label"
                                            for="targetPreviewText"
                                            >{m.plain_text_import_target_preview_label()}</label>
                                        <textarea
                                            value={targetPreview}
                                            readonly
                                            class="form-control"
                                            id="targetPreviewText"
                                            rows="10"
                                            placeholder={m.plain_text_import_target_preview_placeholder()}
                                        ></textarea>
                                    </div>
                                </form>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <div class="modal-footer">
                <button
                    class="btn btn-primary"
                    type="button"
                    id="importTranscriptBtn"
                    onclick={importTranscriptFile}
                    disabled={fileContents && fileContents.length === 0}
                    aria-disabled={fileContents && fileContents.length === 0}>
                    {m.plain_text_import_action_label()}
                </button>
            </div>
        </div>
    </div>
</div>

<style lang="css">
#advancedOptions {
    overflow-y: auto;
    overflow-x: none;
    max-height: 30vh;
}

#srcPreviewText,
#targetPreviewText {
    font-family: monospace;
    white-space: pre-wrap;
}
</style>
